import { Link } from "react-router-dom";
import { Check, Users, Phone, MessageSquare, Clock, Building2, ArrowRight, Calendar } from "lucide-react";
import Reveal from "../Reveal";
import { getActiveDiscount, applyDiscount } from "../../../utils/planDiscounts";

const Stat = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-1.5">
    <Icon className="w-3.5 h-3.5 text-slate-400" />
    <span className="text-[13px] text-slate-600">{label}</span>
  </div>
);

const PlanCard = ({ plan, discount, onBookDemo }) => {
  const basePrice = plan.price;
  const finalPrice = discount ? applyDiscount(basePrice, discount) : basePrice;
  const hasDiscount = discount && finalPrice < basePrice;
  const hasNoCheckoutTarget =
    typeof plan.price !== "number" || plan.price <= 0 || (!plan.stripe_price_id && !plan.stripe_price_id_annual);
  const contactMode = !!plan.is_contact_us || hasNoCheckoutTarget;
  const isPop = !!plan.is_popular;

  return (
    <div
      className={`relative h-full flex flex-col rounded-2xl bg-white p-7 border transition-all duration-200 hover:-translate-y-1 ${
        isPop
          ? "border-[var(--mk-blue)] shadow-[0_20px_40px_-4px_rgba(0,70,190,0.14)] lg:scale-[1.03] z-10"
          : "border-slate-200 shadow-[0_10px_30px_-3px_rgba(15,23,42,0.06)] hover:shadow-[0_16px_36px_-8px_rgba(15,23,42,0.12)]"
      }`}
      data-testid={`home-pricing-card-${plan.plan_id}`}
    >
      {isPop && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.1em] bg-[var(--mk-blue)] text-white shadow-md">
          Most Popular
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-lg font-semibold text-slate-900">{plan.name}</h3>
        {hasDiscount && discount.badge_label && !contactMode && (
          <span className="px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700" data-testid={`home-discount-badge-${plan.plan_id}`}>
            {discount.badge_label}
          </span>
        )}
      </div>

      <div className="mb-5">
        {contactMode ? (
          <span className="text-3xl font-extrabold text-slate-900">{plan.contact_us_text || "Contact us"}</span>
        ) : (
          <>
            {hasDiscount && <span className="text-xl font-bold mr-2 line-through text-slate-300">${basePrice}</span>}
            <span className="text-5xl font-extrabold tracking-tight text-slate-900">${finalPrice}</span>
            <span className="text-[14px] text-slate-500 ml-1">/mo</span>
          </>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2.5 mb-5 pb-5 border-b border-slate-100">
        <Stat icon={Users} label={`${plan.max_users || 2} users`} />
        <Stat icon={Phone} label={`${plan.dedicated_lines || 2} lines`} />
        <Stat icon={MessageSquare} label={`${(plan.sms_limit || 1000).toLocaleString()} SMS`} />
        <Stat icon={Clock} label={`${(plan.minutes_limit || 750).toLocaleString()} min`} />
      </div>

      <ul className="space-y-2.5 mb-7 flex-1">
        {(plan.features || []).slice(0, 5).map((f, fi) => (
          <li key={fi} className="flex items-start gap-2 text-[14px] text-slate-600">
            <Check className="w-4 h-4 shrink-0 mt-0.5 text-[var(--mk-blue)]" />
            {f}
          </li>
        ))}
      </ul>

      {contactMode ? (
        <button onClick={() => onBookDemo?.(plan)} className={`mk-btn w-full ${isPop ? "mk-btn-primary" : "mk-btn-outline"}`} data-testid={`home-pricing-btn-${plan.plan_id}`}>
          <Calendar className="w-4 h-4" /> Book a Demo
        </button>
      ) : (
        <Link to="/register" state={{ selectedPlan: plan.plan_id }} className={`mk-btn w-full ${isPop ? "mk-btn-primary" : "mk-btn-outline"}`} data-testid={`home-pricing-btn-${plan.plan_id}`}>
          Start Free Trial
        </Link>
      )}
    </div>
  );
};

const PricingPreview = ({ plans, discounts, loading, onBookDemo }) => {
  const single = (plans || []).filter((p) => (p.plan_tier || "single") === "single");
  const hasMl = (plans || []).some((p) => p.plan_tier === "multi_location");

  return (
    <section id="pricing" className="bg-[var(--mk-bg-2)] py-16 md:py-24" data-testid="home-pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[var(--mk-blue)] mb-4">Pricing</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-[1.2] mb-4">
            Simple, transparent pricing
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Start free for 7 days — no credit card required. Upgrade, downgrade, or cancel anytime.
          </p>
        </Reveal>

        {!loading && single.length > 0 && (
          <div className={`grid gap-6 ${single.length === 3 ? "md:grid-cols-3" : single.length <= 2 ? "md:grid-cols-2 max-w-3xl mx-auto" : "md:grid-cols-2 lg:grid-cols-3"} mb-12`}>
            {single.map((plan) => (
              <Reveal key={plan.plan_id} className="h-full">
                <PlanCard plan={plan} discount={getActiveDiscount(plan, discounts)} onBookDemo={onBookDemo} />
              </Reveal>
            ))}
          </div>
        )}

        <Reveal className="max-w-3xl mx-auto">
          {hasMl && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8" data-testid="home-ml-teaser">
              <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                <Building2 className="w-5 h-5 text-emerald-700" />
              </div>
              <div className="flex-1">
                <h3 className="text-[16px] font-semibold text-slate-900 mb-0.5">Running multiple locations?</h3>
                <p className="text-[14px] text-slate-600">Multi-location plans with consolidated dashboards, dispatch, and billing.</p>
              </div>
              <Link to="/pricing#multi-location" className="mk-btn mk-btn-sm mk-btn-outline shrink-0" data-testid="home-ml-teaser-link">
                See multi-location plans <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
          <p className="text-center">
            <Link to="/pricing" className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-[var(--mk-blue)] hover:text-[var(--mk-blue-hover)] group" data-testid="home-full-pricing-link">
              Compare every feature on the full pricing page
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default PricingPreview;
